import React, { useEffect, useMemo, useState, useCallback, useRef, FC } from 'react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import _ from 'lodash';
import clsx from 'clsx';

import FixedLoader from '../../../components/FixedLoader/FixedLoader';
import Api from '../../../api';
import useActionApi from '../../../hooks/useActionApi';
import { exitReact } from '../../../utils';
import { JobProps, PartialOffer, HygienistProps, PositionProps, SkillsProps, OfferHygienist } from 'utils/types';
import { formatProfileHygienistName } from '../../../utils';
import { getPaymentTerms, getPaymentMethod } from '../../../utils';
import { formatFirstLicensedLabel } from '../../../utils/educationCredentials';
import { Language } from 'api/Api';

// Components
import Popup from 'components/Popup';
import { OfferTimer } from 'components/OfferTimer';
import TalentResume from '../../../components/TalentResume';
import { CollapsableContainer } from '../../../components/CollapsableContainer';
import Skills from '../CustomOfferDetailsPage/components/Skills';
import WorkExperience from '../CustomOfferDetailsPage/components/WorkExperience';
import AcceptOffer from '../CustomOfferDetailsPage/components/AcceptOffer';
import DeclineOffer from '../CustomOfferDetailsPage/components/DeclineOffer';
import BookAnotherTemp from '../CustomOfferDetailsPage/components/BookAnotherTemp';
import CancelShiftButton from '../CustomOfferDetailsPage/components/CancelShift';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import TimeToLeaveOutlinedIcon from '@mui/icons-material/TimeToLeaveOutlined';
import DiningOutlinedIcon from '@mui/icons-material/DiningOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import BlurLinearOutlinedIcon from '@mui/icons-material/BlurLinearOutlined';
import LineAxisOutlinedIcon from '@mui/icons-material/LineAxisOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WorkIcon from '@mui/icons-material/Work';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';

import heartFilled from '../../../images/heartFilled.svg';
import heartUnfilled from '../../../images/heartUnfilled.svg';
import logoBlue from '../../../images/logoBlue.svg';
import TypographyFilled from '../../../images/Typography.png';
import WorkFilled from '../../../images/WorkFilled.png';
import AccountCircleFilled from '../../../images/AccountCircleFilled.png';

import styles from './CustomOfferPage.module.scss';
import { StyledButton } from '../../../components/Buttons/DefaultButton/DefaultButton.styles';
import { formatPartialOfferTime, formatPartialOfferTimeRange } from '../../../utils/timeFormatting';

dayjs.extend(isToday);

// Extended type for hygienist objects that may have properties from both HygienistProps and OfferHygienist
interface HygienistWithOfferProps extends HygienistProps {
    starScore?: number;
    numRatedJobs?: number;
    languages?: Language[] | null;
    membershipSummary?: {
        officeStatusLabel: string | null;
    } | null;
}

interface JobWithSelOffer extends JobProps {
    selOffer: PartialOffer
}

// Helper function to convert HygienistWithOfferProps to OfferHygienist format
const convertToOfferHygienist = (hygienist: HygienistWithOfferProps | OfferHygienist): OfferHygienist => {
    // If it's already OfferHygienist, return as is
    if ('starScore' in hygienist && typeof hygienist.starScore === 'number' && !('id' in hygienist)) {
        return hygienist as OfferHygienist;
    }
    // Otherwise, convert HygienistWithOfferProps to OfferHygienist
    const h = hygienist as HygienistWithOfferProps;
    return {
        firstName: h.firstName,
        lastName: h.lastName,
        graduationYear: h.graduationYear,
        firstLicensedYear: h.firstLicensedYear ?? null,
        firstLicensedMonth: h.firstLicensedMonth ?? null,
        school: h.school || '',
        CDHONumber: h.CDHONumber?.toString() || '',
        starScore: h.starScore || 0,
        phone: h.phone,
        numBooked: 0,
        resumeUrl: h.resumeUrl,
        province: h.province,
        country: h.country,
        level: h.level || 0,
        photoUrl: h.photoUrl || '',
        isReliable: h.isReliable,
        status: h.status,
        type: h.type,
        completedJobs: h.completedJobs,
        vaxStatus: 0,
        isLeftHanded: h.isLeftHanded,
        skills: h.skills || [],
        about: h.about,
        showForHire: h.showForHire,
        numRatedJobs: h.numRatedJobs,
        languages: h.languages || null,
    };
};

const CustomOfferPage: FC<{}> = () => {
    const history = useHistory();
    const { job: jobData } = history.location.state as { job: JobWithSelOffer };
    const [job, setJob] = useState<JobWithSelOffer>(jobData);
    const [hygienist, setHygienist] = useState<HygienistWithOfferProps | null>(null);
    const [workExperience, setWorkExperience] = useState<PositionProps[]>([]);
    const [latestWorkExperience, setLatestWorkExperience] = useState<PositionProps>();
    const [apiSkills, setApiSkills] = useState<SkillsProps[]>([]);
    const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
    const [isFavouriteHygienist, setIsFavouriteHygienist] = useState(false);
    const [updatingFavourite, setUpdatingFavourite] = useState(false);

    const offer = job.selOffer;
    const hygienistFromOffer = offer?.hygienist;

    // Refs to prevent infinite loops
    const hasFetchedFavourite = useRef<number | null>(null);

    // ===== API CALLS =====
    const handleResumeDataSuccess = useCallback((hygienistData: any) => {
        setHygienist(hygienistData);
        setWorkExperience(hygienistData?.workExperience || []);
        setIsFavouriteHygienist(hygienistData?.isFavouriteDentist || false);
    }, []);

    const handleFavouriteSuccess = useCallback((data: any) => {
        setIsFavouriteHygienist(data?.isFavouriteHygienist ?? false);
    }, []);

    const [getResumeDataLoading, , getResumeData] = useActionApi({
        apiMethod: Api.getResumeData,
        onSuccess: handleResumeDataSuccess
    });

    const [getSkillsLoading, , getSkills] = useActionApi({
        apiMethod: Api.getSkills,
        onSuccess: (skills: SkillsProps[]) => {
            if (_.isEmpty(skills)) return;
            setApiSkills(skills);
        },
    });

    const [checkFavouriteHygienistLoading, , checkFavouriteHygienist] = useActionApi({
        apiMethod: Api.checkFavouriteHygienist,
        onSuccess: handleFavouriteSuccess
    });

    const [isExpired, setIsExpired] = useState<boolean>(false);

    // ===== COMPUTED VALUES =====
    const formattedPostedDate = useMemo(() => {
        return job.postedOn ? dayjs(job.postedOn).format('dddd, MMM D') : '';
    }, [job.postedOn]);

    const formattedShiftDate = useMemo(() => {
        return job.shifts?.[0]?.shiftDate ? dayjs(job.shifts[0].shiftDate).format('dddd, MMM D') : '';
    }, [job.shifts]);

    const shiftTime = useMemo(() => {
        if (!job.shifts?.[0]) return '';
        return `${job.shifts[0].postedStart} - ${job.shifts[0].postedEnd}`;
    }, [job.shifts]);

    const avgRecall = useMemo(() => job.location?.avgApptTime || '30 min', [job.location?.avgApptTime]);

    const hourlyRate = useMemo(() => offer?.hourlyRate, [offer?.hourlyRate]);

    const paymentDescription = useMemo(() => {
        const terms = getPaymentTerms(job.paymentTerms);
        const method = job.paymentMethod
            ? `typically via ${getPaymentMethod(job.paymentMethod, job.location?.country)}`
            : '';
        return `Office Pays: ${terms} ${method}`;
    }, [job.paymentTerms, job.paymentMethod, job.location?.country]);

    const safetyGuidelinesText = useMemo(() => {
        return `This office has confirmed they meet or exceed the ${job.location?.province || ''} guidelines for workplace safety and infection control.`;
    }, [job.location?.province]);

    const hasEndorsements = useMemo(() => {
        return !!job.location?.ppeEndorsed || !!job.location?.friendlyEndorsed || !!job.location?.equipEndorsed;
    }, [job.location]);

    const professionalLicense = useMemo(() => {
        if (!hygienist) return hygienistFromOffer ? `${hygienistFromOffer.firstName} ${hygienistFromOffer.lastName}` : '';
        return formatProfileHygienistName(convertToOfferHygienist(hygienist), hygienist?.skills || []);
    }, [hygienist, hygienistFromOffer]);

    const languages = hygienist?.languages ?? [];
    const languagesToShow = languages.filter((lang: Language) => lang.code !== 'en') ?? [];

    const isPartialOffer = useMemo(() => {
        if (!job?.shifts?.[0] || !offer) {
            return false;
        }

        const offerStart = offer.offeredStartTime;
        const offerEnd = offer.offeredEndTime;
        const shiftStart = job.shifts[0].postedStart;
        const shiftEnd = job.shifts[0].postedEnd;

        return (shiftStart !== offerStart || shiftEnd !== offerEnd);
    }, [job, offer]);

    const { formattedStartTime: formattedPartialStartTime, formattedEndTime: formattedPartialEndTime } = useMemo(() => {
        if (!offer) return { formattedStartTime: '', formattedEndTime: '' };
        
        return formatPartialOfferTimeRange(
            offer.offeredStartTime,
            offer.offeredEndTime,
            offer.offeredStartTimeLocal,
            offer.offeredEndTimeLocal,
            job.location?.lat,
            job.location?.lon
        );
    }, [offer, job.location]);

    const originalShiftHours = useMemo(() => {
        if (!job?.shifts?.[0]) return { startTime: '', endTime: '' };
        return {
            startTime: formatPartialOfferTime(job.shifts[0].postedStart, undefined, job.location?.lat, job.location?.lon),
            endTime: formatPartialOfferTime(job.shifts[0].postedEnd, undefined, job.location?.lat, job.location?.lon)
        };
    }, [job]);

    const partialShiftHours = useMemo(() => {
        return {
            startTime: formattedPartialStartTime,
            endTime: formattedPartialEndTime
        };
    }, [formattedPartialStartTime, formattedPartialEndTime]);

    // ===== EFFECTS =====
    useEffect(() => {
        if (offer?.hygienistId && job.locationId) {
            getResumeData({ hygienistId: offer.hygienistId, locationId: job.locationId, offerId: offer.id });
            if (job.location?.province) {
                getSkills({ type: offer.hygienist?.type || 0, state: job.location.province });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offer?.hygienistId, job.locationId]);

    useEffect(() => {
        const hygienistId = hygienist?.id || offer?.hygienistId;
        if (hygienistId && job.locationId && hasFetchedFavourite.current !== hygienistId) {
            hasFetchedFavourite.current = hygienistId;
            checkFavouriteHygienist({ hygienistId, locationId: job.locationId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hygienist?.id, offer?.hygienistId, job.locationId]);

    useEffect(() => {
        if (offer?.expiresAt) {
            const now = new Date();
            const expiresAt = new Date(offer.expiresAt);
            setIsExpired(expiresAt.getTime() <= now.getTime());
        }
    }, [offer?.expiresAt]);

    useEffect(() => {
        if (workExperience) {
            let latest = workExperience.find((experience) => experience.current);
            if (!latest) {
                latest = workExperience.sort((a, b) => {
                    return dayjs(b.toDate).valueOf() - dayjs(a.toDate).valueOf();
                })[0];
            }
            setLatestWorkExperience(latest);
        }
    }, [workExperience]);

    // ===== HANDLERS =====
    const handleBackNavigation = useCallback(() => {
        history.push('loading');
        exitReact('job-partial', { id: job.id });
    }, [history, job.id]);

    const handleToggleFavourite = async () => {
        // Favourite status is read from resume data
        // Toggle functionality can be added later if API endpoints are available
    };

    const handleViewMap = () => {
        const officeAddress = job.location?.lat + ',+' + job.location?.lon;
        const url = `https://www.google.com/maps/dir/${officeAddress}`;
        if ((window as any).Cordova) {
            (window as any).cordova.InAppBrowser.open(url, '_system', 'location=yes');
        } else {
            window.open(url, '_blank', 'location=yes');
        }
    };

    const handleViewResume = () => {
        if (!hygienist) return;
        if (hygienist.resumeUrl) {
            if ((window as any).Cordova) {
                (window as any).cordova.InAppBrowser.open(hygienist.resumeUrl, '_system', 'location=yes');
            } else {
                window.open(hygienist.resumeUrl, '_blank');
            }
        } else {
            setIsResumeModalOpen(true);
        }
    };

    const bookAnotherHandler = () => {
        history.push('postJob', { date: dayjs(job.startDate).toISOString() });
    };

    // ===== LOADING STATE =====
    const isLoading = getResumeDataLoading || getSkillsLoading || checkFavouriteHygienistLoading || updatingFavourite;

    const displayHygienist: HygienistWithOfferProps | OfferHygienist | undefined = hygienist || hygienistFromOffer;
    if (!displayHygienist) {
        return (
            <div className={styles.pageContainer}>
                <FixedLoader loading={isLoading} />
            </div>
        );
    }
    const officeMembershipStatusLabel = (displayHygienist as HygienistWithOfferProps)?.membershipSummary?.officeStatusLabel || null;

    return (
        <div className={styles.pageContainer}>
            {/* ===== MODALS ===== */}
            <Popup opened={isResumeModalOpen} onClose={() => setIsResumeModalOpen(false)}>
                {hygienist && (
                    <TalentResume
                        hygienist={hygienist}
                        onClose={() => setIsResumeModalOpen(false)}
                    />
                )}
            </Popup>

            {/* ===== HEADER ===== */}
            <header className={styles.header}>
                <div className={styles.headerLeft}>
                    <img src={logoBlue} alt="TempStars" className={styles.logo} />
                </div>
                <div className={styles.headerTitle}>Custom Offer</div>
                <div className={styles.headerActions}>
                    <button className={styles.addShiftButton} aria-label="Add or record a shift" title="Add or record a shift">
                        <span>+</span> Add / Record a Shift
                    </button>
                    <button className={styles.jobBoardButton} aria-label="Job Board" title="Job Board">
                        <CalendarTodayOutlinedIcon />
                        Job Board
                    </button>
                    <button className={styles.menuButton} aria-label="Open menu" title="Open menu">
                        <div className={styles.hamburger}>
                            <span className={styles.hamburgerLine}></span>
                            <span className={styles.hamburgerLine}></span>
                            <span className={styles.hamburgerLine}></span>
                        </div>
                    </button>
                </div>
            </header>

            {/* ===== MAIN CONTENT ===== */}
            <main className={styles.mainContent}>
                {/* ===== SUBHEADER ===== */}
                <div className={styles.subheader}>
                    <button className={styles.backButton} onClick={handleBackNavigation} aria-label="Go back" title="Go back">
                        <ArrowBackIcon />
                    </button>
                    <div className={styles.subheaderActions}>
                        <div className={styles.offerPendingBadge}>
                            <div className={styles.offerPendingDot}></div>
                            Offer Pending
                        </div>
                        {offer?.expiresAt && (
                            <div className={styles.offerExpireBadge}>
                                <div className={styles.offerExpireDot}></div>
                                Offer Expire
                            </div>
                        )}
                        <button className={styles.locationButton} onClick={handleViewMap} aria-label="View location on map" title="View location on map">
                            <RoomOutlinedIcon />
                            Location
                        </button>
                        <button
                            className={styles.heartButton}
                            onClick={handleToggleFavourite}
                            disabled={true}
                            title={isFavouriteHygienist ? 'Favourite hygienist' : 'Not a favourite'}
                        >
                            <img
                                src={isFavouriteHygienist ? heartFilled : heartUnfilled}
                                alt={isFavouriteHygienist ? 'Favourite hygienist' : 'Not a favourite'}
                                className={styles.heartIcon}
                            />
                        </button>
                    </div>
                </div>

                {/* Main Content Box - wraps everything from office name to bottom */}
                <div className={styles.mainContentBox}>
                    {/* Shift Details Card - Light Grey Background */}
                    <div className={styles.shiftDetailsCard}>
                        <div className={styles.shiftDateRow}>
                            <div className={styles.shiftDate}>
                                <CalendarTodayOutlinedIcon className={styles.shiftDateIcon} />
                                {formattedShiftDate}
                            </div>
                        </div>
                        <div className={styles.shiftLocationRow}>
                            <div className={styles.shiftLocation}>
                                {job.location?.address},
                                <br />
                                {job.location?.city}, {job.location?.province} {job.location?.postalCode}
                            </div>
                            <div className={styles.shiftTimes}>
                                <div className={styles.shiftTimeItem}>
                                    <span className={styles.shiftTimeLabel}>Start:</span>
                                    <span className={styles.shiftTimeValue}>{job.shifts?.[0]?.postedStart}</span>
                                </div>
                                <div className={styles.shiftTimeItem}>
                                    <span className={styles.shiftTimeLabel}>End:</span>
                                    <span className={styles.shiftTimeValue}>{job.shifts?.[0]?.postedEnd}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Custom Offer Details Card - White Background */}
                    <div className={styles.customOfferCard}>
                        <div className={styles.customOfferHeader}>
                            <h2 className={styles.customOfferTitle}>Custom Offer Details</h2>
                        </div>

                        {/* Hygienist Profile Section */}
                        <div className={styles.profileSection}>
                            {displayHygienist.photoUrl && (
                                <img 
                                    src={displayHygienist.photoUrl} 
                                    alt={professionalLicense}
                                    className={styles.profileImage}
                                />
                            )}
                            <div className={styles.profileInfo}>
                                <div className={styles.profileNameRow}>
                                    <h3 className={styles.profileName}>{professionalLicense}</h3>
                                    <img
                                        src={isFavouriteHygienist ? heartFilled : heartUnfilled}
                                        alt={isFavouriteHygienist ? 'Remove from favourites' : 'Add to favourites'}
                                        className={styles.heartIconSmall}
                                    />
                                    {officeMembershipStatusLabel && (
                                        <span className={styles.membershipStatusBadge}>{officeMembershipStatusLabel}</span>
                                    )}
                                </div>
                                {displayHygienist.graduationYear && displayHygienist.school && (
                                    <p className={styles.graduatedText}>
                                        Graduated {displayHygienist.graduationYear} from {displayHygienist.school}
                                    </p>
                                )}
                                {formatFirstLicensedLabel(displayHygienist.firstLicensedYear, displayHygienist.firstLicensedMonth) && (
                                    <p className={styles.graduatedText}>
                                        {formatFirstLicensedLabel(displayHygienist.firstLicensedYear, displayHygienist.firstLicensedMonth)}
                                    </p>
                                )}
                                {displayHygienist.isLeftHanded !== undefined && (
                                    <p className={styles.leftHanded}>
                                        {displayHygienist.firstName} is {displayHygienist.isLeftHanded ? 'Left' : 'Right'} handed
                                    </p>
                                )}
                                <div className={styles.hourlyRateSection}>
                                    <span className={styles.hourlyRateLabel}>Hourly Rate</span>
                                    <span className={styles.hourlyRateValue}>${hourlyRate}/hr</span>
                                </div>
                                {/* Ratings */}
                                {(displayHygienist.numRatedJobs ?? 0) > 0 ? (
                                    <div className={styles.ratingsSection}>
                                        <div className={styles.stars}>
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <span
                                                    key={star}
                                                    className={
                                                        star <= Math.floor(displayHygienist.starScore || 0)
                                                            ? styles.starFilled
                                                            : styles.starEmpty
                                                    }
                                                >
                                                    ★
                                                </span>
                                            ))}
                                        </div>
                                        <span className={styles.ratingsCount}>
                                            {displayHygienist.numRatedJobs ?? 0} {(displayHygienist.numRatedJobs ?? 0) === 1 ? 'Rating' : 'Ratings'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className={styles.ratingsSection}>
                                        <span className={styles.noRatings}>No Rated Shifts yet</span>
                                    </div>
                                )}
                                {offer?.expiresAt && (
                                    <div className={styles.offerExpiry}>
                                        <OfferTimer expiresAt={offer.expiresAt} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons - Pill Shaped */}
                        <div className={styles.actionButtonsRow}>
                            {offer && offer.id && !isExpired && (
                                <>
                                    <AcceptOffer
                                        offerData={{
                                            isPartialHours: isPartialOffer,
                                            partialOffer: offer,
                                            hygienist: displayHygienist ? convertToOfferHygienist(displayHygienist) : hygienistFromOffer,
                                            skills: hygienist?.skills || []
                                        }}
                                        offerId={offer.id}
                                        originalShiftHours={originalShiftHours}
                                        partialShiftHours={partialShiftHours}
                                    />
                                    <DeclineOffer
                                        partialOffer={offer}
                                        offerId={offer.id}
                                    />
                                    <StyledButton
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        startIcon={<DescriptionIcon />}
                                        className={`${styles.viewResumeButton} resumeButton`}
                                        onClick={handleViewResume}
                                    >
                                        View Resume
                                    </StyledButton>
                                </>
                            )}
                        </div>

                        {/* Job-Seeking Status Section */}
                        {offer?.showJobSeekingStatus === 1 && displayHygienist.showForHire === 1 && displayHygienist.firstName && (
                            <div className={styles.jobSeekingStatusSection}>
                                <p className={styles.jobSeekingStatusTitle}>{displayHygienist.firstName}'s Job-Seeking Status:</p>
                                <div className={styles.jobSeekingStatusContent}>
                                    <WorkOutlineIcon className={styles.jobSeekingIcon} />
                                    <span className={styles.jobSeekingStatusText}>Actively Seeking Permanent Employment</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Skills & Qualification Section */}
                    {(hygienist?.CDHONumber || hygienist?.skills?.length || languagesToShow.length) && (
                        <CollapsableContainer
                            title="Skills & Qualification (self-reported)"
                            defaultIsOpen={true}
                            icon={TypographyFilled}
                            backgroundColor="success"
                            noBorder={true}
                        >
                            {hygienist?.CDHONumber && (
                                <p className={styles.licenseInfo}>
                                    License #: {hygienist.CDHONumber}
                                </p>
                            )}
                            {languagesToShow.length > 0 && (
                                <>
                                    <p className={styles.sectionLabel}>Languages:</p>
                                    <p className={styles.sectionValue}>
                                        {languagesToShow.map((l: any) => l.name).join(', ')}
                                    </p>
                                </>
                            )}
                            {hygienist?.skills && hygienist.skills.length > 0 && hygienist.type !== undefined && (
                                <>
                                    <p className={styles.sectionLabel}>Training & Certifications received:</p>
                                    <Skills 
                                        apiSkills={apiSkills} 
                                        skills={hygienist.skills} 
                                        type={hygienist.type} 
                                        province={hygienist.province}
                                    />
                                </>
                            )}
                        </CollapsableContainer>
                    )}

                    {/* Work Experience Section */}
                    {workExperience?.length > 0 && (
                        <CollapsableContainer
                            title="Work Experience"
                            defaultIsOpen={true}
                            icon={WorkFilled}
                            backgroundColor="alternate"
                            noBorder={true}
                            preview={latestWorkExperience && <WorkExperience experiences={[latestWorkExperience]} />}
                            fullPreview={true}
                        >
                            <WorkExperience experiences={workExperience} />
                        </CollapsableContainer>
                    )}

                    {/* About Me Section */}
                    {hygienist?.about && (
                        <CollapsableContainer
                            title="About Me"
                            defaultIsOpen={true}
                            icon={AccountCircleFilled}
                            backgroundColor="main"
                            noBorder={true}
                            preview={hygienist.about}
                        >
                            <p className={styles.aboutText}>{hygienist.about}</p>
                        </CollapsableContainer>
                    )}

                    {/* Friendly Reminder Section */}
                    <div className={styles.reminderSection}>
                        <div className={styles.reminderHeader}>
                            <WarningIcon className={styles.warningIcon} />
                            <span className={styles.reminderTitle}>A friendly reminder</span>
                        </div>
                        <p className={styles.reminderText}>
                            Standard placement fees apply to shifts booked with {displayHygienist.firstName} - 
                            whether arranged on the app or through direct contact
                        </p>
                    </div>

                    {/* Bottom Action Buttons */}
                    <div className={styles.bottomButtons}>
                        <BookAnotherTemp job={job} />
                        <CancelShiftButton job={job} />
                    </div>
                </div>
                {/* End Main Content Box */}
            </main>

            <FixedLoader loading={isLoading} />
        </div>
    );
};

export default CustomOfferPage;
